import { useState, useEffect, useMemo } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api, { getProperties } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, TrendingUp, TrendingDown, Clock, Users, Package } from "lucide-react";
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from "recharts";

export default function AdminAnalytics() {
  const [properties, setProperties] = useState([]);
  const [selectedSource, setSelectedSource] = useState("all");

  // Mode & Configurator State
  const [compareMode, setCompareMode] = useState(false);
  const [compareType, setCompareType] = useState("sources"); // "sources" or "timeframes"
  
  // Single Mode State
  const [singleProperty, setSingleProperty] = useState("all");
  const [singleTimeframe, setSingleTimeframe] = useState("30");
  
  // Compare Mode State (Sources)
  const [sourceA, setSourceA] = useState("all");
  const [sourceB, setSourceB] = useState("all");
  const [sharedTimeframe, setSharedTimeframe] = useState("30");

  // Compare Mode State (Timeframes)
  const [timeframeA, setTimeframeA] = useState("30");
  const [timeframeB, setTimeframeB] = useState("prev_period");
  const [sharedProperty, setSharedProperty] = useState("all");

  // Data State
  const [data, setData] = useState(null); // Dataset A
  const [compareData, setCompareData] = useState(null); // Dataset B
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProperties();
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    compareMode, compareType, 
    singleProperty, singleTimeframe,
    sourceA, sourceB, sharedTimeframe,
    timeframeA, timeframeB, sharedProperty,
    selectedSource
  ]);

  const loadProperties = async () => {
    try {
      const data = await getProperties();
      setProperties(data);
    } catch (error) {
      toast.error("Failed to load properties");
    }
  };

  const calculateDateRange = (tfString, baseStart = null) => {
    const end = baseStart ? new Date(baseStart) : new Date();
    const start = new Date(end);
    
    if (tfString === "7") start.setDate(start.getDate() - 7);
    else if (tfString === "30") start.setDate(start.getDate() - 30);
    else if (tfString === "90") start.setDate(start.getDate() - 90);
    else if (tfString === "365") start.setDate(start.getDate() - 365);
    else if (tfString === "prev_period") {
      // Not typically called this way, handled separately
      start.setDate(start.getDate() - 30);
    }
    else if (tfString === "prev_year") {
       start.setFullYear(start.getFullYear() - 1);
       end.setFullYear(end.getFullYear() - 1);
    }
    return { start, end };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      let urlA = `/analytics/timeseries?unit=day`;
      let urlB = `/analytics/timeseries?unit=day`;
      
      let propA = "all", propB = "all";
      let startA, endA, startB, endB;

      if (!compareMode) {
        // Single Mode
        propA = singleProperty;
        const range = calculateDateRange(singleTimeframe);
        startA = range.start; endA = range.end;
        
        urlA += `&start_date=${startA.toISOString()}&end_date=${endA.toISOString()}`;
        if (propA !== 'all') urlA += `&property_ids=${propA}`;
        
        const resA = await api.get(urlA);
        setData(resA.data);
        setCompareData(null);
      } else {
        // Compare Mode
        if (compareType === "sources") {
          propA = sourceA;
          propB = sourceB;
          const range = calculateDateRange(sharedTimeframe);
          startA = range.start; endA = range.end;
          startB = new Date(startA); endB = new Date(endA);
        } else {
          // Timeframes
          propA = sharedProperty;
          propB = sharedProperty;
          
          const rangeA = calculateDateRange(timeframeA);
          startA = rangeA.start; endA = rangeA.end;
          
          if (timeframeB === "prev_period") {
            endB = new Date(startA);
            endB.setDate(endB.getDate() - 1);
            startB = new Date(endB);
            const daysDiff = Math.floor((endA - startA) / (1000 * 60 * 60 * 24));
            startB.setDate(startB.getDate() - daysDiff);
          } else if (timeframeB === "prev_year") {
            startB = new Date(startA); startB.setFullYear(startB.getFullYear() - 1);
            endB = new Date(endA); endB.setFullYear(endB.getFullYear() - 1);
          } else {
            const rangeB = calculateDateRange(timeframeB);
            startB = rangeB.start; endB = rangeB.end;
          }
        }

        urlA += `&start_date=${startA.toISOString()}&end_date=${endA.toISOString()}`;
        if (propA !== 'all') urlA += `&property_ids=${propA}`;
        
        urlB += `&start_date=${startB.toISOString()}&end_date=${endB.toISOString()}`;
        if (propB !== 'all') urlB += `&property_ids=${propB}`;

        const [resA, resB] = await Promise.all([
          api.get(urlA),
          api.get(urlB)
        ]);

        setData(resA.data);
        setCompareData(resB.data);
      }
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    toast.success("CSV Export started...");
  };

  const calculatePeakHour = (sourceData) => {
    if (!sourceData?.hourly_trends?.length) return 'N/A';
    const sorted = [...sourceData.hourly_trends].sort((a, b) => b.orders - a.orders);
    return sorted[0]?.orders > 0 ? `${sorted[0].hour}:00` : 'N/A';
  };

  const currentStats = useMemo(() => {
    if (!data) return { rev: 0, orders: 0, appTime: 0, delTime: 0, peakHour: 'N/A', items: [] };
    const s = data.summary;
    let rev = s.net_revenue || 0;
    let orders = s.total_orders || 0;
    
    if (selectedSource === "food") {
        rev = data.timeseries?.reduce((sum, d) => sum + (d.food_revenue || 0), 0) || 0;
        orders = data.timeseries?.reduce((sum, d) => sum + (d.food_orders || 0), 0) || 0;
    } else if (selectedSource === "experience") {
        rev = data.timeseries?.reduce((sum, d) => sum + (d.exp_revenue || 0), 0) || 0;
        orders = data.timeseries?.reduce((sum, d) => sum + (d.exp_bookings || 0), 0) || 0;
    } else if (selectedSource === "request") {
        rev = 0;
        orders = s.total_requests || 0;
    }

    let items = data.item_performance || [];
    if (selectedSource !== "all" && selectedSource !== "request") {
        // Handle mapping mismatch: "food" vs "experience" vs what backend returns ("food", "exp")
        const typeMatch = selectedSource === "experience" ? "exp" : selectedSource;
        items = items.filter(i => i.type === typeMatch);
    }

    return { 
      rev, 
      orders, 
      appTime: s.avg_approval_time || 0, 
      delTime: s.avg_delivery_time || 0,
      peakHour: calculatePeakHour(data),
      items: items.slice(0, 50)
    };
  }, [data, selectedSource]);

  const compareStats = useMemo(() => {
    if (!compareData) return { rev: 0, orders: 0, appTime: 0, delTime: 0, peakHour: 'N/A', items: [] };
    const s = compareData.summary;
    let rev = s.net_revenue || 0;
    let orders = s.total_orders || 0;
    
    if (selectedSource === "food") {
        rev = compareData.timeseries?.reduce((sum, d) => sum + (d.food_revenue || 0), 0) || 0;
        orders = compareData.timeseries?.reduce((sum, d) => sum + (d.food_orders || 0), 0) || 0;
    } else if (selectedSource === "experience") {
        rev = compareData.timeseries?.reduce((sum, d) => sum + (d.exp_revenue || 0), 0) || 0;
        orders = compareData.timeseries?.reduce((sum, d) => sum + (d.exp_bookings || 0), 0) || 0;
    } else if (selectedSource === "request") {
        rev = 0;
        orders = s.total_requests || 0;
    }
    return { 
      rev, 
      orders, 
      appTime: s.avg_approval_time || 0, 
      delTime: s.avg_delivery_time || 0,
      peakHour: calculatePeakHour(compareData),
      items: [] 
    };
  }, [compareData, selectedSource]);

  // Merge chart data for Recharts
  const mergedChartData = useMemo(() => {
    const arr = [];
    if (data?.timeseries) {
      data.timeseries.forEach((day, index) => {
        const compDay = compareData?.timeseries?.[index];
        arr.push({
          date: day.date.substring(5),
          current_rev: (day.food_revenue || 0) + (day.exp_revenue || 0),
          compare_rev: compDay ? ((compDay.food_revenue || 0) + (compDay.exp_revenue || 0)) : 0,
          current_orders: (day.food_orders || 0) + (day.exp_bookings || 0),
          compare_orders: compDay ? ((compDay.food_orders || 0) + (compDay.exp_bookings || 0)) : 0,
          avg_del_time: day.avg_delivery_time || 0,
          compare_del_time: compDay ? (compDay.avg_delivery_time || 0) : 0,
        });
      });
    }
    return arr;
  }, [data, compareData]);

  const sources = [
    { id: 'all', label: 'All Sources' },
    { id: 'food', label: 'Food & Beverage' },
    { id: 'experience', label: 'Experiences' },
    { id: 'request', label: 'Service Requests' }
  ];

  const getPropertyName = (id) => id === 'all' ? 'All Properties' : properties.find(p => p.property_id === id)?.name || 'Property';

  return (
    <AdminLayout title="Intelligence Analytics">
      <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="admin-analytics">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Intelligence Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">Deep dive into historical performance, operational delays, and items.</p>
          </div>
          <Button onClick={exportCSV} variant="outline" className="h-[40px] bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 shadow-sm rounded-lg mt-4 md:mt-0">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>

        {/* Unified Premium Configurator Box */}
        <div className="bg-white rounded-xl shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] border border-gray-100 p-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            
            {/* Primary Setting based on Mode */}
            {!compareMode ? (
              <div className="flex flex-wrap items-center gap-6">
                <div className="w-56">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Property</label>
                  <Select value={singleProperty} onValueChange={setSingleProperty}>
                    <SelectTrigger className="h-10 bg-gray-50 border-gray-200">
                      <SelectValue placeholder="Select Property" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Company-wise (All)</SelectItem>
                      {properties.map(p => (
                        <SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Timeframe</label>
                  <Select value={singleTimeframe} onValueChange={setSingleTimeframe}>
                    <SelectTrigger className="h-10 bg-gray-50 border-gray-200">
                      <SelectValue placeholder="Select Timeframe" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 Days</SelectItem>
                      <SelectItem value="30">Last 30 Days</SelectItem>
                      <SelectItem value="90">Last 90 Days</SelectItem>
                      <SelectItem value="365">Last Year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="w-full">
                  <label className="text-[10px] font-bold text-[#00897B] uppercase tracking-wider mb-1.5 block">Compare Type</label>
                  <Select value={compareType} onValueChange={setCompareType}>
                    <SelectTrigger className="h-10 border-[#00897B]/20 text-[#00897B] font-semibold bg-[#E0F2F1]/30">
                      <SelectValue placeholder="Compare Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sources">Compare Two Properties</SelectItem>
                      <SelectItem value="timeframes">Compare Two Timeframes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {compareType === 'sources' ? (
                  <>
                    <div className="w-full">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Source A (Primary)</label>
                      <Select value={sourceA} onValueChange={setSourceA}>
                        <SelectTrigger className="h-10 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Company-wise (All)</SelectItem>
                          {properties.map(p => (<SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-2"><span className="text-gray-300">vs</span> Source B</label>
                      <Select value={sourceB} onValueChange={setSourceB}>
                        <SelectTrigger className="h-10 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Company-wise (All)</SelectItem>
                          {properties.map(p => (<SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">In Timeframe</label>
                      <Select value={sharedTimeframe} onValueChange={setSharedTimeframe}>
                        <SelectTrigger className="h-10 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">Last 7 Days</SelectItem>
                          <SelectItem value="30">Last 30 Days</SelectItem>
                          <SelectItem value="90">Last 90 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-full">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Timeframe A (Primary)</label>
                      <Select value={timeframeA} onValueChange={setTimeframeA}>
                        <SelectTrigger className="h-10 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">Last 7 Days</SelectItem>
                          <SelectItem value="30">Last 30 Days</SelectItem>
                          <SelectItem value="90">Last 90 Days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-2"><span className="text-gray-300">vs</span> Timeframe B</label>
                      <Select value={timeframeB} onValueChange={setTimeframeB}>
                        <SelectTrigger className="h-10 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prev_period">Previous Equivalent Period</SelectItem>
                          <SelectItem value="prev_year">Same Dates Last Year</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-full">
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">For Property</label>
                      <Select value={sharedProperty} onValueChange={setSharedProperty}>
                        <SelectTrigger className="h-10 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Company-wise (All)</SelectItem>
                          {properties.map(p => (<SelectItem key={p.property_id} value={p.property_id}>{p.property_name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Toggle switch always on the right */}
            <div className="flex items-center gap-3 bg-gray-50 px-4 h-10 rounded-full border border-gray-200 shrink-0 ml-auto md:ml-6 mt-4 md:mt-0">
              <span className="text-sm font-semibold text-gray-700">Compare Mode</span>
              <Switch checked={compareMode} onCheckedChange={setCompareMode} />
            </div>

          </div>
        </div>

        {/* Source / Type Pills */}
        <div className="flex flex-wrap gap-2">
          {sources.map(src => (
            <button
              key={src.id}
              onClick={() => setSelectedSource(src.id)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                selectedSource === src.id 
                  ? 'bg-[#00897B] text-white shadow-md' 
                  : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {src.label}
            </button>
          ))}
        </div>

        {loading && !data ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00897B]"></div>
          </div>
        ) : !data ? (
          <div className="text-center py-20 text-gray-500">Failed to load data</div>
        ) : (
          <div className="space-y-6">
            
            {/* KPI Cards / Split Views */}
            {!compareMode ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-gray-200 shadow-sm rounded-xl overflow-hidden">
                  <CardContent className="p-6">
                    <p className="text-sm font-medium text-gray-500 mb-2">Net Revenue</p>
                    <h3 className="text-3xl font-bold text-gray-900">₹{currentStats.rev.toLocaleString()}</h3>
                  </CardContent>
                </Card>
                <Card className="border-gray-200 shadow-sm rounded-xl overflow-hidden">
                  <CardContent className="p-6">
                    <p className="text-sm font-medium text-gray-500 mb-2">Total Volume</p>
                    <h3 className="text-3xl font-bold text-[#00897B]">{currentStats.orders.toLocaleString()}</h3>
                  </CardContent>
                </Card>
                <Card className="border-gray-200 shadow-sm rounded-xl overflow-hidden">
                  <CardContent className="p-6">
                    <p className="text-sm font-medium text-gray-500 mb-2">Avg Delivery Time</p>
                    <h3 className="text-3xl font-bold text-[#EF4444]">{currentStats.delTime} <span className="text-lg font-normal text-gray-500">mins</span></h3>
                  </CardContent>
                </Card>
                <Card className="border-gray-200 shadow-sm rounded-xl overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-medium text-gray-500">Global Avg Approval</p>
                        <span className="bg-purple-50 text-purple-700 text-[10px] px-2 py-0.5 rounded-full font-bold">Peak: {currentStats.peakHour}</span>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{currentStats.appTime} <span className="text-lg font-normal text-gray-500">mins</span></h3>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Dataset A */}
                <div className="bg-white rounded-xl border border-blue-100 shadow-sm overflow-hidden relative">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-blue-500"></div>
                  <div className="p-4 flex items-center justify-between border-b border-gray-50 bg-blue-50/20">
                    <h3 className="font-bold text-blue-900 text-sm uppercase tracking-wider">
                      {compareType === 'sources' ? getPropertyName(sourceA) : `Timeframe A (${timeframeA}d)`}
                    </h3>
                    <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">Dataset A</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 p-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Net Revenue</p>
                      <h3 className="text-2xl font-bold text-gray-900">₹{currentStats.rev.toLocaleString()}</h3>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Total Volume</p>
                      <h3 className="text-2xl font-bold text-gray-900">{currentStats.orders.toLocaleString()}</h3>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Avg Delivery</p>
                      <h3 className="text-xl font-bold text-gray-900">{currentStats.delTime} <span className="text-sm font-normal text-gray-500">mins</span></h3>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Avg Approval</p>
                      <h3 className="text-xl font-bold text-gray-900">{currentStats.appTime} <span className="text-sm font-normal text-gray-500">mins</span></h3>
                    </div>
                  </div>
                </div>

                {/* Dataset B */}
                <div className="bg-white rounded-xl border border-green-100 shadow-sm overflow-hidden relative">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-[#10B981]"></div>
                  <div className="p-4 flex items-center justify-between border-b border-gray-50 bg-[#10B981]/5">
                    <h3 className="font-bold text-[#047857] text-sm uppercase tracking-wider">
                      {compareType === 'sources' ? getPropertyName(sourceB) : `Timeframe B (${timeframeB})`}
                    </h3>
                    <span className="text-[10px] font-bold text-[#047857] bg-[#D1FAE5] px-2 py-1 rounded-full">Dataset B</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 p-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Net Revenue</p>
                      <h3 className="text-2xl font-bold text-gray-900">₹{compareStats.rev.toLocaleString()}</h3>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Total Volume</p>
                      <h3 className="text-2xl font-bold text-gray-900">{compareStats.orders.toLocaleString()}</h3>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Avg Delivery</p>
                      <h3 className="text-xl font-bold text-gray-900">{compareStats.delTime} <span className="text-sm font-normal text-gray-500">mins</span></h3>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Avg Approval</p>
                      <h3 className="text-xl font-bold text-gray-900">{compareStats.appTime} <span className="text-sm font-normal text-gray-500">mins</span></h3>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
              
              <Card className="border-gray-200 shadow-sm rounded-xl">
                <CardHeader className="pb-2 border-b border-gray-50">
                  <CardTitle className="text-sm font-bold text-gray-800">
                    Volume Trend Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={mergedChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{fontSize: 10, fill: '#888'}} axisLine={false} tickLine={false} />
                        <YAxis tick={{fontSize: 10, fill: '#888'}} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                        <Legend iconType="circle" wrapperStyle={{fontSize: '12px'}}/>
                        <Line type="monotone" dataKey="current_orders" name={compareMode ? "Dataset A" : "Current Volume"} stroke="#3B82F6" strokeWidth={2} dot={false} activeDot={{r: 6}} />
                        {compareMode && (
                          <Line type="monotone" dataKey="compare_orders" name="Dataset B" stroke="#10B981" strokeWidth={2} dot={false} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-sm rounded-xl">
                <CardHeader className="pb-2 border-b border-gray-50">
                  <CardTitle className="text-sm font-bold text-gray-800">Revenue Generation</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={mergedChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{fontSize: 10, fill: '#888'}} axisLine={false} tickLine={false} />
                        <YAxis tick={{fontSize: 10, fill: '#888'}} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{fill: '#f9fafb'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                        <Legend iconType="circle" wrapperStyle={{fontSize: '12px'}}/>
                        <Bar dataKey="current_rev" name={compareMode ? "Dataset A Revenue" : "Revenue"} fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        {compareMode && (
                          <Bar dataKey="compare_rev" name="Dataset B Revenue" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            

            {/* Bottom Tables */}
            <div className="grid grid-cols-1 gap-6 mt-8">
              <Card className="border-gray-200 shadow-sm rounded-xl">
                <CardHeader className="pb-2 border-b border-gray-50 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-bold text-gray-800">Dataset A: Item Lifecycle Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-[11px] text-gray-500 uppercase bg-white border-b border-gray-100">
                        <tr>
                          <th className="px-6 py-4 font-bold">Item Name</th>
                          <th className="px-6 py-4 font-bold">Category</th>
                          <th className="px-6 py-4 font-bold text-right">Volume</th>
                          <th className="px-6 py-4 font-bold text-right">Revenue</th>
                          <th className="px-6 py-4 font-bold text-center">Approval Rate</th>
                          <th className="px-6 py-4 font-bold text-right">Avg Appr. Time</th>
                          <th className="px-6 py-4 font-bold text-right">Avg Del. Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 bg-white">
                        {currentStats.items.length === 0 ? <tr><td colSpan="7" className="text-center py-8 text-gray-400">No items found for Dataset A.</td></tr> : currentStats.items.map((item, i) => (
                          <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                            <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
                            <td className="px-6 py-4">
                              <span className={`px-2.5 py-1 text-[10px] font-bold uppercase rounded-md ${item.type === 'food' ? 'bg-[#E0F2F1] text-[#00695C]' : 'bg-[#F3E5F5] text-[#6A1B9A]'}`}>
                                {item.type}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right font-medium">{item.qty}</td>
                            <td className="px-6 py-4 text-right font-medium text-gray-600">₹{item.revenue.toLocaleString()}</td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full ${item.approval_ratio < 80 ? 'bg-[#EF4444]' : 'bg-[#10B981]'}`} style={{width: `${item.approval_ratio}%`}}></div>
                                </div>
                                <span className="text-xs font-medium text-gray-600 w-8">{item.approval_ratio}%</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right text-gray-600 font-medium">
                              {item.avg_app_time > 0 ? `${item.avg_app_time}m` : '-'}
                            </td>
                            <td className="px-6 py-4 text-right text-gray-600 font-medium">
                              {item.avg_del_time > 0 ? `${item.avg_del_time}m` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

          </div>
        )}
      </div>
    </AdminLayout>
  );
}
